import { fiberStarted as fiberStarted$1, fiberSuccesses as fiberSuccesses$1, fiberFailures as fiberFailures$1, fiberLifetimes as fiberLifetimes$1, fiberActive as fiberActive$1 } from '../../internal/fiberRuntime.esm.js';
import { MetricTypeId as MetricTypeId$1, globalMetricRegistry as globalMetricRegistry$1, make as make$1, mapInput as mapInput$1, counter as counter$1, frequency as frequency$1, withConstantInput as withConstantInput$1, fromMetricKey as fromMetricKey$1, gauge as gauge$1, histogram as histogram$1, increment as increment$1, incrementBy as incrementBy$1, map as map$1, mapType as mapType$1, set as set$1, snapshot as snapshot$1, succeed as succeed$1, sync as sync$1, summary as summary$1, summaryTimestamp as summaryTimestamp$1, tagged as tagged$1, taggedWithLabelsInput as taggedWithLabelsInput$1, taggedWithLabels as taggedWithLabels$1, timer as timer$1, timerWithBoundaries as timerWithBoundaries$1, trackAll as trackAll$1, trackDefect as trackDefect$1, trackDefectWith as trackDefectWith$1, trackDuration as trackDuration$1, trackDurationWith as trackDurationWith$1, trackError as trackError$1, trackErrorWith as trackErrorWith$1, trackSuccess as trackSuccess$1, trackSuccessWith as trackSuccessWith$1, update as update$1, value as value$1, withNow as withNow$1, zip as zip$1, unsafeSnapshot as unsafeSnapshot$1 } from '../../internal/metric.esm.js';

/**
 * @since 2.0.0
 */
/**
 * @since 2.0.0
 * @category symbols
 */
const MetricTypeId = MetricTypeId$1;

/**
 * @since 2.0.0
 * @category symbols
 */

/**
 * A `Metric<Type, In, Out>` represents a concurrent metric which accepts
 * updates of type `In` and are aggregated to a stateful value of type `Out`.
 *
 * For example, a counter metric would have type `Metric<number, number>`,
 * representing the fact that the metric can be updated with numbers (the amount
 * to increment or decrement the counter by), and the state of the counter is a
 * number.
 *
 * There are five primitive metric types supported by Effect:
 *
 *   - Counters
 *   - Frequencies
 *   - Gauges
 *   - Histograms
 *   - Summaries
 *
 * @since 2.0.0
 * @category models
 */

/**
 * @since 2.0.0
 * @category models
 */

/**
 * @since 2.0.0
 */

/**
 * @since 2.0.0
 * @category globals
 */
const globalMetricRegistry = globalMetricRegistry$1;

/**
 * @since 2.0.0
 * @category constructors
 */
const make = make$1;

/**
 * Returns a new metric that is powered by this one, but which accepts updates
 * of the specified new type, which must be transformable to the input type of
 * this metric.
 *
 * @since 2.0.0
 * @category mapping
 */
const mapInput = mapInput$1;

/**
 * Represents a Counter metric that tracks cumulative numerical values over time.
 * Counters can be incremented and decremented and provide a running total of changes.
 *
 * @param name - The name of the counter metric.
 * @param options - An optional configuration for the counter:
 * - description - A description of the counter.
 * - bigint - Indicates if the counter uses 'bigint' data type.
 * - incremental - Set to 'true' for a counter that only increases. With this configuration, Effect ensures that non-incremental updates have no impact on the counter, making it exclusively suitable for counting upwards.
 *
 * @example
 * import * as Metric from "effect/Metric"
 *
 * const numberCounter = Metric.counter("count", {
 *   description: "A number counter"
 * });
 *
 * const bigintCounter = Metric.counter("count", {
 *   description: "A bigint counter",
 *   bigint: true
 * });
 *
 * @since 2.0.0
 * @category constructors
 */
const counter = counter$1;

/**
 * Creates a Frequency metric to count occurrences of events.
 * Frequency metrics are used to count the number of times specific events or incidents occur.
 *
 * @param name - The name of the Frequency metric.
 * @param description - An optional description of the Frequency metric.
 *
 * @example
 * import * as Metric from "effect/Metric"
 *
 * const errorFrequency = Metric.frequency("error_frequency", "Counts the occurrences of errors.");
 *
 * @since 2.0.0
 * @category constructors
 */
const frequency = frequency$1;

/**
 * Returns a new metric that is powered by this one, but which accepts updates
 * of any type, and translates them to updates with the specified constant
 * update value.
 *
 * @since 2.0.0
 * @category constructors
 */
const withConstantInput = withConstantInput$1;

/**
 * @since 2.0.0
 * @category constructors
 */
const fromMetricKey = fromMetricKey$1;

/**
 * Represents a Gauge metric that tracks and reports a single numerical value at a specific moment.
 * Gauges are suitable for metrics that represent instantaneous values, such as memory usage or CPU load.
 *
 * @param name - The name of the gauge metric.
 * @param options - An optional configuration for the gauge:
 * - description - A description of the gauge metric.
 * - bigint - Indicates if the counter uses 'bigint' data type.
 *
 * @example
 * import * as Metric from "effect/Metric"
 *
 * const numberGauge = Metric.gauge("memory_usage", {
 *   description: "A gauge for memory usage"
 * });
 *
 * const bigintGauge = Metric.gauge("cpu_load", {
 *   description: "A gauge for CPU load",
 *   bigint: true
 * });
 *
 * @since 2.0.0
 * @category constructors
 */
const gauge = gauge$1;

/**
 * Represents a Histogram metric that records observations in specified value boundaries.
 * Histogram metrics are useful for measuring the distribution of values within a range.
 *
 * @param name - The name of the histogram metric.
 * @param boundaries - The boundaries for defining the histogram's value ranges.
 * @param description - A description of the histogram metric.
 *
 * @example
 * import * as Metric from "effect/Metric"
 * import * as MetricBoundaries from "effect/MetricBoundaries"
 *
 * const latencyHistogram = Metric.histogram("latency_histogram",
 *   MetricBoundaries.linear({ start: 0, width: 10, count: 11 }),
 *   "Measures the distribution of request latency."
 * );
 *
 * @since 2.0.0
 * @category constructors
 */
const histogram = histogram$1;

/**
 * @since 2.0.0
 * @category aspects
 */
const increment = increment$1;

/**
 * @since 2.0.0
 * @category aspects
 */
const incrementBy = incrementBy$1;

/**
 * Returns a new metric that is powered by this one, but which outputs a new
 * state type, determined by transforming the state type of this metric by the
 * specified function.
 *
 * @since 2.0.0
 * @category mapping
 */
const map = map$1;

/**
 * @since 2.0.0
 * @category mapping
 */
const mapType = mapType$1;

/**
 * @since 2.0.0
 * @category aspects
 */
const set = set$1;

/**
 * Captures a snapshot of all metrics recorded by the application.
 *
 * @since 2.0.0
 * @category getters
 */
const snapshot = snapshot$1;

/**
 * Creates a metric that ignores input and produces constant output.
 *
 * @since 2.0.0
 * @category constructors
 */
const succeed = succeed$1;

/**
 * Creates a metric that ignores input and produces constant output.
 *
 * @since 2.0.0
 * @category constructors
 */
const sync = sync$1;

/**
 * Creates a Summary metric that records observations and calculates quantiles.
 * Summary metrics provide statistical information about a set of values, including quantiles.
 *
 * @param options - An object with configuration options for the Summary metric:
 * - name - The name of the Summary metric.
 * - maxAge - The maximum age of observations to retain.
 * - maxSize - The maximum number of observations to keep.
 * - error - The error percentage when calculating quantiles.
 * - quantiles - An `Chunk` of quantiles to calculate (e.g., [0.5, 0.9]).
 * - description - An optional description of the Summary metric.
 *
 * @example
 * import * as Metric from "effect/Metric"
 * import * as Chunk from "effect/Chunk"
 *
 * const responseTimesSummary = Metric.summary({
 *   name: "response_times_summary",
 *   maxAge: "60 seconds", // Retain observations for 60 seconds.
 *   maxSize: 1000, // Keep a maximum of 1000 observations.
 *   error: 0.01, // Allow a 1% error when calculating quantiles.
 *   quantiles: Chunk.make(0.5, 0.9, 0.99), // Calculate 50th, 90th, and 99th percentiles.
 *   description: "Measures the distribution of response times."
 * });
 *
 * @since 2.0.0
 * @category constructors
 */
const summary = summary$1;

/**
 * @since 2.0.0
 * @category constructors
 */
const summaryTimestamp = summaryTimestamp$1;

/**
 * Returns a new metric, which is identical in every way to this one, except
 * the specified tags have been added to the tags of this metric.
 *
 * @since 2.0.0
 * @category utils
 */
const tagged = tagged$1;

/**
 * Returns a new metric, which is identical in every way to this one, except
 * dynamic tags are added based on the update values. Note that the metric
 * returned by this method does not return any useful information, due to the
 * dynamic nature of the added tags.
 *
 * @since 2.0.0
 * @category utils
 */
const taggedWithLabelsInput = taggedWithLabelsInput$1;

/**
 * Returns a new metric, which is identical in every way to this one, except
 * the specified tags have been added to the tags of this metric.
 *
 * @since 2.0.0
 * @category utils
 */
const taggedWithLabels = taggedWithLabels$1;

/**
 * Creates a timer metric, based on a histogram, which keeps track of
 * durations in milliseconds. The unit of time will automatically be added to
 * the metric as a tag (i.e. `"time_unit: milliseconds"`).
 *
 * @since 2.0.0
 * @category constructors
 */
const timer = timer$1;

/**
 * Creates a timer metric, based on a histogram created from the provided
 * boundaries, which keeps track of durations in milliseconds. The unit of time
 * will automatically be added to the metric as a tag (i.e.
 * `"time_unit: milliseconds"`).
 *
 * @since 2.0.0
 * @category constructors
 */
const timerWithBoundaries = timerWithBoundaries$1;

/**
 * Returns an aspect that will update this metric with the specified constant
 * value every time the aspect is applied to an effect, regardless of whether
 * that effect fails or succeeds.
 *
 * @since 2.0.0
 * @category aspects
 */
const trackAll = trackAll$1;

/**
 * Returns an aspect that will update this metric with the defects of the
 * effects that it is applied to.
 *
 * @since 2.0.0
 * @category aspects
 */
const trackDefect = trackDefect$1;

/**
 * Returns an aspect that will update this metric with the result of applying
 * the specified function to the defect throwables of the effects that the
 * aspect is applied to.
 *
 * @since 2.0.0
 * @category aspects
 */
const trackDefectWith = trackDefectWith$1;

/**
 * Returns an aspect that will update this metric with the duration that the
 * effect takes to execute. To call this method, the input type of the metric
 * must be `Duration`.
 *
 * @since 2.0.0
 * @category aspects
 */
const trackDuration = trackDuration$1;

/**
 * Returns an aspect that will update this metric with the duration that the
 * effect takes to execute. To call this method, you must supply a function
 * that can convert the `Duration` to the input type of this metric.
 *
 * @since 2.0.0
 * @category aspects
 */
const trackDurationWith = trackDurationWith$1;

/**
 * Returns an aspect that will update this metric with the failure value of
 * the effects that it is applied to.
 *
 * @since 2.0.0
 * @category aspects
 */
const trackError = trackError$1;

/**
 * Returns an aspect that will update this metric with the result of applying
 * the specified function to the error value of the effects that the aspect is
 * applied to.
 *
 * @since 2.0.0
 * @category aspects
 */
const trackErrorWith = trackErrorWith$1;

/**
 * Returns an aspect that will update this metric with the success value of
 * the effects that it is applied to.
 *
 * @since 2.0.0
 * @category aspects
 */
const trackSuccess = trackSuccess$1;

/**
 * Returns an aspect that will update this metric with the result of applying
 * the specified function to the success value of the effects that the aspect is
 * applied to.
 *
 * @since 2.0.0
 * @category aspects
 */
const trackSuccessWith = trackSuccessWith$1;

/**
 * Updates the metric with the specified update message. For example, if the
 * metric were a counter, the update would increment the method by the
 * provided amount.
 *
 * @since 2.0.0
 * @category utils
 */
const update = update$1;

/**
 * Retrieves a snapshot of the value of the metric at this moment in time.
 *
 * @since 2.0.0
 * @category getters
 */
const value = value$1;

/**
 * @since 2.0.0
 * @category utils
 */
const withNow = withNow$1;

/**
 * @since 2.0.0
 * @category zipping
 */
const zip = zip$1;

/**
 * Unsafely captures a snapshot of all metrics recorded by the application.
 *
 * @since 2.0.0
 * @category unsafe
 */
const unsafeSnapshot = unsafeSnapshot$1;

/**
 * @since 2.0.0
 * @category metrics
 */
const fiberStarted = fiberStarted$1;

/**
 * @since 2.0.0
 * @category metrics
 */
const fiberSuccesses = fiberSuccesses$1;

/**
 * @since 2.0.0
 * @category metrics
 */
const fiberFailures = fiberFailures$1;

/**
 * @since 2.0.0
 * @category metrics
 */
const fiberLifetimes = fiberLifetimes$1;

/**
 * @since 2.0.0
 * @category metrics
 */
const fiberActive = fiberActive$1;

export { MetricTypeId, counter, fiberActive, fiberFailures, fiberLifetimes, fiberStarted, fiberSuccesses, frequency, fromMetricKey, gauge, globalMetricRegistry, histogram, increment, incrementBy, make, map, mapInput, mapType, set, snapshot, succeed, summary, summaryTimestamp, sync, tagged, taggedWithLabels, taggedWithLabelsInput, timer, timerWithBoundaries, trackAll, trackDefect, trackDefectWith, trackDuration, trackDurationWith, trackError, trackErrorWith, trackSuccess, trackSuccessWith, unsafeSnapshot, update, value, withConstantInput, withNow, zip };
